/* eslint-disable no-restricted-globals */
import React from 'react';

import { IObservableValue, makeAutoObservable, observable } from 'mobx';
import { z } from 'zod';

import { validateLength } from 'src/api/utils/form2Validators';
import { Common } from 'src/AppState/Common';
import { PopupsStackState } from 'src/AppState/PopupsStackState';
import {
    isNumericWholeNumber,
    validateCsvIsRequired,
    validateIf,
    validIsUnique,
} from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.validators';
import { FormHelper } from 'src/domains/casino/webview/bonus-engine/campaign/FormHelper';
import { EligibleUsersTableState } from 'src/domains/casino/webview/bonus-engine/campaign/player-management/eligible-users/EligibleUsersTable.state';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';

const EligibleUsersZod = z.array(z.number());
export type EligibleUsersType = z.TypeOf<typeof EligibleUsersZod>;

export class EligibleUsersState {
    private static observableData: IObservableValue<EligibleUsersType> = observable.box([]);

    public playerIdManualInput = FormInputState.new('')
        .map(validIsUnique(() => this.eligibleUsers.map(String)))
        .map((value) =>
            validateIf(
                () => value !== '',
                () => isNumericWholeNumber(false, 'Player ID must be a whole number.')(value)
            )
        )
        .map((value) =>
            validateIf(
                () => value !== '',
                () => validateLength(15, 'Player ID must contain no more than 15 digits')(value)
            )
        );

    public playerIdsCsvForm;

    private form = FormHelper.form;

    public tableState = new EligibleUsersTableState();

    public constructor(private readonly common: Common) {
        EligibleUsersState.observableData = observable.box([]);

        this.playerIdsCsvForm = FormInputState.new(this.eligibleUsers).map(
            validateCsvIsRequired('Player ID is required.')
        );

        makeAutoObservable(this);
    }

    // Common

    public static get(): EligibleUsersType {
        return this.observableData.get();
    }

    public get eligibleUsers(): EligibleUsersType {
        return EligibleUsersState.get();
    }

    private set eligibleUsers(ids: EligibleUsersType) {
        EligibleUsersState.observableData.set(ids);
    }

    public get totalAdded(): number {
        return this.eligibleUsers.length;
    }

    public get formErrorsCount(): number {
        return this.playerIdsCsvForm.errorsForView.length;
    }

    public setFormValues = (ids: EligibleUsersType): void => {
        this.eligibleUsers = ids;
        this.playerIdsCsvForm.setValue(ids);
    };

    private setValueOnChange(ids: EligibleUsersType): void {
        this.eligibleUsers = ids;
        this.playerIdsCsvForm.setValueOnChange(ids);
    }

    public removePlayerId = (id: number): void => {
        const ids = this.eligibleUsers;
        const idsTemp = ids.filter((value) => value !== id);

        this.setValueOnChange(idsTemp);
    };

    public removeAllPlayerIds = (): void => {
        this.clearFilters();

        this.setValueOnChange([]);
        this.playerIdsCsvForm.setAsVisited();
    };

    public validateForm(): boolean {
        this.playerIdsCsvForm.setAsVisited();

        return this.playerIdsCsvForm.result.value.type === 'ok';
    }

    public isTouched(): boolean {
        return this.form.isTouched();
    }

    public get isCsvFormTouched(): boolean {
        return this.playerIdsCsvForm.isTouched();
    }

    public get isManualFormTouched(): boolean {
        return this.playerIdManualInput.isTouched();
    }

    public setIsTouched(): void {
        if (this.isCsvFormTouched && this.isTouched() === false) {
            FormHelper.setIsTouched(this.form);
        }
        this.resetPlayerIdInput();
        this.clearFilters();
    }

    public resetIsTouched(): void {
        this.playerIdManualInput.resetIsTouched();
        this.playerIdsCsvForm.resetIsTouched();

        FormHelper.resetIsTouched(this.form);
    }

    public resetIsVisited(): void {
        this.playerIdManualInput.resetIsVisited();
        this.playerIdsCsvForm.resetIsVisited();
    }

    public resetPlayerIdInput(): void {
        this.playerIdManualInput.setValue('');
    }

    public clearFilters(): void {
        this.tableState.clearFilters();
    }

    public handleClearAllPlayerIds = (): void => {
        const popupsStack = PopupsStackState.get(this.common);
        popupsStack.addPopup('clear_all_player_ids', {
            template: 'generic',
            title: 'Clear all Player IDs',
            message: (
                <p className='m-0'>Are you sure you want to clear all Player IDs? This action can not be undone.</p>
            ),
            hasOverlay: true,
            cancel_label: 'Cancel',
            confirm_label: 'Continue',
            onConfirm: () => {
                this.removeAllPlayerIds();

                popupsStack.closePopupOnTop();
            },
            onCancel: () => {
                popupsStack.closePopupOnTop();
            },
        });
    };

    // Manual Upload

    public addPlayerId = (): void => {
        const ids = [...this.eligibleUsers, parseInt(this.playerIdManualInput.value, 10)];

        this.resetPlayerIdInput();
        this.setValueOnChange(ids);
    };

    public get additionDisabled(): boolean {
        return this.playerIdManualInput.value === '' || this.playerIdManualInput.result.value.type === 'error';
    }

    // CSV Upload

    public fileToTable = (ids: EligibleUsersType): void => {
        this.setValueOnChange(ids);
    };
}
